import { datadogLogs } from '@datadog/browser-logs';

const BASE_DELAY_MS = 1000;
const MAX_DELAY_MS = 60 * 1000;
// A connection must stay open this long before the backoff resets, so a socket that
// opens and immediately drops keeps backing off instead of retrying every second.
const STABLE_CONNECTION_MS = 30 * 1000;

const logSocketEvent = (message, context) => {
  console.log(`Socket: ${message}`, context);
  datadogLogs.logger.info(`websocket ${message}`, { websocket: context });
};

/**
 * Opens a WebSocket that reconnects with exponential backoff and jitter.
 * Only one socket is ever live per instance: events from a replaced socket are ignored,
 * so a stale socket can never trigger another reconnect or deliver duplicate messages.
 * @param {string} url
 * @param {{ storeId: string|number, onMessage: (event: MessageEvent) => void }} options
 * @returns {{ close: (code?: number, reason?: string) => void }}
 */
export const createReconnectingSocket = (url, { storeId, onMessage }) => {
  let ws = null;
  let openedAt = null;
  let attempts = 0;
  let reconnectTimer = null;
  let closedByClient = false;

  const scheduleReconnect = () => {
    if (closedByClient || reconnectTimer) return;

    const backoff = Math.min(MAX_DELAY_MS, BASE_DELAY_MS * 2 ** attempts);
    const delay = Math.round(backoff / 2 + Math.random() * (backoff / 2));
    attempts++;

    logSocketEvent('reconnect scheduled', { storeId, attempt: attempts, delayMs: delay });
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      open();
    }, delay);
  };

  const open = () => {
    const socket = new WebSocket(url);
    ws = socket;

    socket.onopen = () => {
      if (socket !== ws) return;
      openedAt = Date.now();
      logSocketEvent('connected', { storeId, attempt: attempts });
    };

    socket.onmessage = (event) => {
      if (socket !== ws) return;
      onMessage(event);
    };

    socket.onerror = () => {
      if (socket !== ws) return;
      // The browser always follows an error with a close event, which handles the reconnect.
      logSocketEvent('error', { storeId });
    };

    socket.onclose = (event) => {
      if (socket !== ws) return;
      const openForMs = openedAt === null ? 0 : Date.now() - openedAt;
      ws = null;
      openedAt = null;
      if (openForMs >= STABLE_CONNECTION_MS) {
        attempts = 0;
      }

      logSocketEvent('closed', { storeId, code: event.code, reason: event.reason, wasClean: event.wasClean, openForMs });
      scheduleReconnect();
    };
  };

  const close = (code = 1000, reason) => {
    closedByClient = true;
    clearTimeout(reconnectTimer);
    reconnectTimer = null;

    const socket = ws;
    ws = null;
    socket?.close(code, reason);
  };

  open();
  return { close };
};
